import { ICommentEditorProps, useCommentsUI } from '@cscglobal-tbs/collaboration-ui';
import './CommentRichTextEditor.css';

import { Button, Spinner } from '@fluentui/react-components';
import {
    ChangeEventArgs,
    Count,
    HtmlEditor,
    Inject,
    PasteCleanup,
    QuickToolbar,
    RichTextEditorComponent,
    Toolbar,
} from '@syncfusion/ej2-react-richtexteditor';
import { RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentMentionComponent } from './CommentMentionComponent';
import { mentionParser } from './mentionParser';

export interface ICommentRichTextEditorValue {
    commentText: string;
    commentHtml: string;
    userMentions: Array<string>;
}

export const CommentRichTextEditor = (props: ICommentEditorProps) => {
    const { t } = useTranslation();
    const editorRef: RefObject<RichTextEditorComponent> = useRef<RichTextEditorComponent>(null);
    const { state: uiState } = useCommentsUI();
    const [sendDisabled, setSendDisabled] = useState(true);

    // Get text values from translations
    const commentInputAriaLabel = t('commentInputAriaLabel').toString();
    const commentInputPlaceholder = t('commentInputPlaceholder').toString();
    const saveButtonText = t('sendCommentButtonText').toString();
    const saveButtonAriaLabel = saveButtonText;
    const cancelButtonText = t('cancel').toString();
    const processingText = t('sendingCommentButtonText').toString();

    // there should only be one of CommentRichTextEditor open at a time, but if we have issues,
    // we can useId to distinguish multiples. not doing that yet because not disposing of them grows the DOM.
    const rteIdentifier = `ctx-comment-rte`;
    const mentionTargetIdentifier = `#${rteIdentifier}_rte-edit-view`;

    function getCommentRichTextEditorSaveValue(): ICommentRichTextEditorValue {
        const editorComponentRef: RichTextEditorComponent = editorRef.current!;
        const mentions = mentionParser(editorComponentRef.value);
        return {
            commentText: editorComponentRef.getText(),
            commentHtml: editorComponentRef.value,
            userMentions: mentions,
        };
    }

    function handleSaveButtonClick() {
        props.onSave(getCommentRichTextEditorSaveValue());
    }

    function actionBegin(args: any) {
        // disables drag and drop so users cannot upload files or images yet
        if (args.type === 'drop' || args.type === 'dragstart') {
            args.cancel = true;
        }
        // if the user hits enter while in the mention component, this stops it from creating a newline
        // we might need a check for if the popup is open, but for now this seems to work without it and the user
        // can still hit enter while in the editor and see a newline. don't ask me, it's syncfusion.
        if (args.requestType === 'EnterAction') {
            args.cancel = true;
        }
    }

    function handleChange(changeEventArgs: ChangeEventArgs) {
        const editorComponentRef: RichTextEditorComponent = editorRef.current!;
        const newValue = editorComponentRef.getText();
        // an empty value is null, not an empty string
        // the value is html so if the user types only spaces you end up with a value of <p>&nbsp;</p>
        // so sadly we need to check the text property of the control to see if it's all whitespace
        const hasContent = newValue.trim() !== '' && changeEventArgs.value !== props.commentHtml;
        setSendDisabled(!hasContent);

        if (props.onChange) {
            props.onChange(hasContent);
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
                {/* There is no keydown event to consume or any other way to know the user has made changes
                other than relying on the change event, which doesn't fire very fast. little hack to set
                autosave with a very short interval. Change won't be invoked if there are no changes from the last time it
                checked, per their docs.
                Since this is replacing a textfield that was invoking onChange on every keypress, it SHOULD be ok.
                Also, we want showCharCount, but setting it to true bombs ever jest render; there is an open ticket with SF 
                Also, readonly doesn't seem to work. (there is a disabled property but it's not documented and also doesn't work) */}

                <RichTextEditorComponent
                    quickToolbarSettings={{ enable: false }}
                    autoSaveOnIdle={true}
                    saveInterval={200}
                    change={handleChange}
                    htmlAttributes={{ 'aria-label': commentInputAriaLabel }}
                    showCharCount={true}
                    toolbarSettings={{ items: ['Bold', 'Italic', 'Underline', '|', 'Undo', 'Redo'] }}
                    pasteCleanupSettings={{
                        keepFormat: false,
                        plainText: true,
                        prompt: false,
                    }}
                    maxLength={1000}
                    ref={editorRef}
                    id={rteIdentifier}
                    placeholder={commentInputPlaceholder}
                    actionBegin={actionBegin}
                    value={props.commentHtml || ''}
                    readOnly={uiState.isSubmitting}
                >
                    <Inject services={[HtmlEditor, Toolbar, PasteCleanup, Count, QuickToolbar]} />
                </RichTextEditorComponent>
                <CommentMentionComponent targetIdentifier={mentionTargetIdentifier} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '5px' }}>
                <Button
                    appearance='primary'
                    aria-label={saveButtonAriaLabel}
                    onClick={handleSaveButtonClick}
                    disabled={Boolean(sendDisabled || uiState.isSubmitting)}
                >
                    {uiState.isSubmitting ? processingText : saveButtonText}
                    {uiState.isSubmitting && <Spinner size='extra-tiny' />}
                </Button>
                <Button appearance='secondary' onClick={props.onCancel} aria-label={cancelButtonText}>
                    {cancelButtonText}
                </Button>
            </div>
        </div>
    );
};
